import { FunctionComponent, useEffect } from "react";
import { observer } from "mobx-react-lite";

import { FileUploadComponent, IFileType } from "../../components/Shared";
import { FeatureLayout } from "../../components/Shared/Layouts/FeatureLayout/FeatureLayout";
import { possibleFileTypes } from "../../components/Shared/Models/Constants/wellKnownFileTypes.const";
import { translationKeys } from "../../models/app-translation-keys";
import { UploadFile } from "../../models/classes/upload-file.class";
import { outputLanguageOptions } from "../../models/constants/language-options.const";
import { ILanguageOption } from "../../models/interfaces/language-options.interface";
import { IProgressedFileResult } from "../../models/interfaces/progressed-file-result.interface";
import { useStore } from "../../stores/StoreContext";
import { SummaryUpload } from "./SummaryUpload";

const RawSummaryPage: FunctionComponent = () => {
  const { userStore, budgetStore, summaryStore } = useStore();

  const allowedFileTypes: IFileType[] = [
    possibleFileTypes.pdf,
    possibleFileTypes.doc,
    possibleFileTypes.docx,
    possibleFileTypes.txt,
  ];

  const summaryTranslationKeys = translationKeys.feature.summary;

  const summaryUploadItem = (
    uploadFile: UploadFile,
    setIsFileProcessing: (id: string, isProcessing: boolean) => void,
    downloadFunction: (file: UploadFile, content: IProgressedFileResult) => void,
  ) => (
    <SummaryUpload
      disabled={budgetStore.hasBudget === false}
      uploadFile={uploadFile}
      setIsFileProcessing={setIsFileProcessing}
      downloadFunction={downloadFunction}
      languageOptions={outputLanguageOptions}
      initialLanguage={getInitialLanguage()}
    />
  );

  const getInitialLanguage = (): ILanguageOption => {
    return (
      outputLanguageOptions.find((language: ILanguageOption) => language.code === userStore.currentLanguage.code) ??
      outputLanguageOptions[0]
    );
  };

  return (
    <FeatureLayout
      titleKey={summaryTranslationKeys.title}
      subTitleKey={summaryTranslationKeys.subTitle}
      descriptionKey={summaryTranslationKeys.description}
      hasPendingChanges={summaryStore.isAnyFileProgressing}
    >
      <FileUploadComponent
        files={summaryStore.uploadFiles}
        onChange={(files: any[]) => (summaryStore.uploadFiles = files)}
        disabled={budgetStore.hasBudget === false}
        showAllowedFileTypes={false}
        allowedFileTypes={allowedFileTypes}
        fileSizeLimitMB={100}
        maxConcurrentProcessedFiles={5}
        uploadTemplate={summaryUploadItem}
        fileUploadAreaStyles={{ minHeight: "18rem" }}
      />
    </FeatureLayout>
  );
};

export const SummaryPage = observer(RawSummaryPage);
